import React, { Ref, SyntheticEvent } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { Link as RouterLink } from 'react-router-dom';
import { compose, space, typography, color, display } from 'styled-system';
import shouldForwardProp from '@styled-system/should-forward-prop';

import { hideFocusRingOnClick, forwardInnerRef } from 'localmed-core';
import { Space, Typography } from '../../../../diTheme/diEmotionTheme';
import { ThemeProps } from '../../../../types';

interface StyleProps extends ThemeProps {
  color?: string;
}

const StyledAnchor: any = styled('a', { shouldForwardProp })(
  hideFocusRingOnClick,

  ({ theme, color: currColor }: StyleProps) => css`
    label: base;
    background: transparent;
    border: 0;
    margin: 0;
    padding: 0;
    cursor: pointer;
    color: ${theme.colors.blue};
    font-size: ${theme.fontSizes.sm};
    transition-timing-function: ease-out;
    transition-duration: 500ms;
    transition-property: background-color, border-color, color;
    text-align: inherit;

    &:link,
    &:visited {
      text-decoration: none;
    }

    &:hover,
    &:active {
      color: ${currColor || theme.colors.blue};
      transition-duration: 200ms;
      /* !important use to override style from _diDefaults.scss */
      text-decoration: underline !important;
    }

    &:disabled {
      color: ${theme.colors.blue};
      cursor: not-allowed;
    }
  `,
  compose(space, typography, color, display)
);

export type LinkProps = Space &
  Typography & {
    /** Navigate using react-router. */
    to?: string | any;
    /** Navigate using the browser (full page load). */
    href?: string;
    /** Checks Whether to Navigate using the browser (full page load). */
    fullNavigation?: boolean;
    /** When you use an `onClick` handler, a `<button>` tag will be used, but
     * styled to look like a link.
     */
    onClick?: (event: SyntheticEvent<any>) => void;
    innerRef?: Ref<any>;
  };

function Link({ onClick, to, href, fullNavigation, innerRef, ...props }: LinkProps) {
  let as;
  let type;

  if (onClick != null && !href) {
    as = 'button';
    type = 'button';
  } else if (to != null) {
    if (fullNavigation) {
      href = to;
    } else {
      as = RouterLink;
    }
  }

  return (
    <StyledAnchor
      as={as}
      type={type}
      {...props}
      to={to}
      href={href}
      onClick={onClick}
      ref={innerRef}
    />
  );
}

export default forwardInnerRef<LinkProps, void>(Link);
